// frontend/src/pages/SurveyBuilder.jsx
import React, { useState, useContext, useEffect, useMemo } from "react";
import { AuthContext } from "../context/AuthContext";
import { useNavigate, useParams } from "react-router-dom";
import { authHeaders } from "../utils/authHeaders";
import "./SurveyBuilder.css";

const API = (process.env.REACT_APP_API_URL || "/api").replace(/\/$/, "");

async function readJsonSafe(res) {
  const ct = (res.headers.get("content-type") || "").toLowerCase();
  if (!ct.includes("application/json")) {
    const t = await res.text();
    throw new Error(`Non-JSON (${res.status}): ${t.slice(0, 160)}`);
  }
  return res.json();
}

const QUESTION_TYPES = [
  { value: "text", label: "Text" },
  { value: "number", label: "Number (Integer)" },
  { value: "decimal", label: "Decimal" },
  { value: "calculation", label: "Kalkulasi" },
  { value: "date", label: "Date" },
  { value: "select_one", label: "Select One" },
  { value: "select_multiple", label: "Select Multiple" },
  { value: "gps", label: "GPS" },
  { value: "media", label: "Media" },
];

const SURVEY_CHANNELS = [
  {
    value: "web",
    label: "Web Form",
    description: "Untuk survey lengkap: GPS, media, tanggal, decimal, kalkulasi, dan logic kompleks.",
  },
  {
    value: "whatsapp_simple",
    label: "WhatsApp Survey",
    description: "Untuk survey sederhana: text, angka, select one, dan select multiple.",
  },
];

const WHATSAPP_SIMPLE_TYPES = ["text", "number", "select_one", "select_multiple"];

const OPERATORS = [
  { value: "equals", label: "Equals" },
  { value: "not_equals", label: "Not equals" },
  { value: "greater", label: "Greater than" },
  { value: "less", label: "Less than" },
  { value: "contains", label: "Contains" },
];

const ACTIONS = [
  { value: "show", label: "Show this question" },
  { value: "hide", label: "Hide this question" },
  { value: "calculate", label: "Calculate (auto)" },
];

const makeQuestionKey = () => {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  return `q_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
};

const emptyQuestion = () => ({
  client_key: makeQuestionKey(),
  question_text: "",
  type: "text",
  options: [],
  logics: [], // rules attached to THIS question (based on previous questions)
  calculation: "", // optional hint/legacy
  required: false,
});

const SurveyBuilder = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const { id } = useParams(); // edit mode if exists

  const [loading, setLoading] = useState(false);

  const [survey, setSurvey] = useState({
    title: "",
    description: "",
    survey_channel: "web",
    questions: [],
  });

  const [currentQuestion, setCurrentQuestion] = useState(emptyQuestion());
  const [editingIndex, setEditingIndex] = useState(null); // null = add mode
  const [dragIndex, setDragIndex] = useState(null);

  const [optionInput, setOptionInput] = useState("");

  // logic inputs (for currentQuestion)
  // question_key remains stable when questions are reordered or removed.
  const [logicCondition, setLogicCondition] = useState({
    question_key: "",
    operator: "equals",
    value: "",
  });

  const [logicAction, setLogicAction] = useState({
    action: "show",
    value: "", // only used for calculate (formula)
  });

  const parseMaybeJSON = (v, fb) => {
    if (v == null) return fb;
    if (Array.isArray(v) || typeof v === "object") return v;
    try {
      return JSON.parse(v) ?? fb;
    } catch {
      return fb;
    }
  };

  const attachLogicKeys = (logics, questions) => {
    return (Array.isArray(logics) ? logics : []).map((lg) => {
      const questionKey = lg?.if?.question_key;
      if (questionKey) return lg;

      const legacyNo = Number(lg?.if?.question_id);
      const referenced = Number.isFinite(legacyNo) ? questions[legacyNo - 1] : null;
      if (!referenced?.client_key) return lg;

      return {
        ...lg,
        if: {
          ...lg.if,
          question_key: referenced.client_key,
        },
      };
    });
  };

  const refreshLegacyQuestionIds = (logics, questions) => {
    return (Array.isArray(logics) ? logics : []).map((lg) => {
      const questionKey = lg?.if?.question_key;
      const referencedIndex = questionKey
        ? questions.findIndex((q) => q.client_key === questionKey)
        : -1;

      return {
        ...lg,
        if: {
          ...lg.if,
          question_id: referencedIndex >= 0 ? referencedIndex + 1 : lg?.if?.question_id,
        },
      };
    });
  };

  // ---- derived helpers
  const isSelectType = useMemo(() => {
    return (
      currentQuestion.type === "select_one" ||
      currentQuestion.type === "select_multiple"
    );
  }, [currentQuestion.type]);

  const isCalculationType = currentQuestion.type === "calculation";

  const activeQuestionTypes = useMemo(() => {
    if (survey.survey_channel === "whatsapp_simple") {
      return QUESTION_TYPES.filter((t) => WHATSAPP_SIMPLE_TYPES.includes(t.value));
    }
    return QUESTION_TYPES;
  }, [survey.survey_channel]);

  const hasUnsupportedWhatsAppQuestions = useMemo(() => {
    if (survey.survey_channel !== "whatsapp_simple") return false;
    return survey.questions.some((q) => !WHATSAPP_SIMPLE_TYPES.includes(q.type));
  }, [survey.questions, survey.survey_channel]);

  const questionChoicesForLogic = useMemo(() => {
    // logic should reference previous questions that already exist in survey.questions
    return survey.questions.map((q, idx) => ({
      value: q.client_key,
      label: `Q${idx + 1} - ${q.question_text || "(untitled)"}`,
      type: q.type,
      options: Array.isArray(q.options) ? q.options : [],
    }));
  }, [survey.questions]);

  // ---------- Load for edit mode ----------
  useEffect(() => {
    const load = async () => {
      if (!id) return;
      setLoading(true);
      try {
        const res = await fetch(`${API}/surveys/${id}`, {
          headers: {
            ...authHeaders(user, { Accept: "application/json" }),
          },
          credentials: "include",
        });
        const data = await readJsonSafe(res);
        if (!res.ok || !data?.success) {
          throw new Error(data?.message || `HTTP ${res.status}`);
        }

        const s = data.survey || {};
        const qs = Array.isArray(data.questions) ? data.questions : [];

        const mapped = qs.map((q) => ({
          client_key: q.client_key || `db_${q.id}`,
          question_text: q.question_text || "",
          type: q.type || "text",
          options: parseMaybeJSON(q.options, []),
          logics: parseMaybeJSON(q.logic, []), // backend field: logic
          calculation: q.calculation || "",
          required: !!q.required,
        }));
        const mappedWithLogicKeys = mapped.map((q) => ({
          ...q,
          logics: attachLogicKeys(q.logics, mapped),
        }));

        setSurvey({
          title: s.title || "",
          description: s.description || "",
          survey_channel: s.survey_channel || "web",
          questions: mappedWithLogicKeys,
        });

        // reset editor
        setCurrentQuestion(emptyQuestion());
        setEditingIndex(null);
        setOptionInput("");
        setLogicCondition({ question_key: "", operator: "equals", value: "" });
        setLogicAction({ action: "show", value: "" });
      } catch (e) {
        console.error("Load survey error:", e);
        alert("Gagal memuat survey.");
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [id, user?.role]); // eslint-disable-line react-hooks/exhaustive-deps

  // ---------- Options ----------
  const addOption = () => {
    const v = optionInput.trim();
    if (!v) return;
    setCurrentQuestion((p) => ({ ...p, options: [...p.options, v] }));
    setOptionInput("");
  };

  const removeOption = (i) => {
    setCurrentQuestion((p) => ({
      ...p,
      options: p.options.filter((_, idx) => idx !== i),
    }));
  };

  // ---------- Logic ----------
  const addLogic = () => {
    if (!logicCondition.question_key) return;
    if (!String(logicCondition.value || "").trim()) return;
    const referencedIndex = survey.questions.findIndex((q) => q.client_key === logicCondition.question_key);

    const newLogic = {
      if: {
        question_key: logicCondition.question_key,
        // legacy fallback for older preview/submission code
        question_id: referencedIndex >= 0 ? referencedIndex + 1 : null,
        operator: logicCondition.operator,
        value: logicCondition.value,
      },
      then: {
        action: logicAction.action,
        value: logicAction.action === "calculate" ? logicAction.value : "",
      },
    };

    setCurrentQuestion((p) => ({ ...p, logics: [...p.logics, newLogic] }));

    // reset logic form
    setLogicCondition({ question_key: "", operator: "equals", value: "" });
    setLogicAction({ action: "show", value: "" });
  };

  const removeLogic = (i) => {
    setCurrentQuestion((p) => ({
      ...p,
      logics: p.logics.filter((_, idx) => idx !== i),
    }));
  };

  // ---------- Add / Update Question ----------
  const validateCurrentQuestion = () => {
    if (!currentQuestion.question_text.trim()) {
      alert("Question text wajib diisi.");
      return false;
    }

    if (isSelectType && currentQuestion.options.length === 0) {
      alert("Untuk Select One/Multiple, options tidak boleh kosong.");
      return false;
    }

    if (isCalculationType && !String(currentQuestion.calculation || "").trim()) {
      alert("Tipe Kalkulasi membutuhkan formula, misalnya Q1 + Q2.");
      return false;
    }

    if (
      survey.survey_channel === "whatsapp_simple" &&
      !WHATSAPP_SIMPLE_TYPES.includes(currentQuestion.type)
    ) {
      alert("WhatsApp Survey hanya mendukung Text, Number, Select One, dan Select Multiple.");
      return false;
    }

    // If action calculate exists, formula should exist
    const hasCalcLogic = (currentQuestion.logics || []).some(
      (lg) => lg?.then?.action === "calculate"
    );
    if (hasCalcLogic) {
      const anyFormulaEmpty = (currentQuestion.logics || []).some(
        (lg) => lg?.then?.action === "calculate" && !String(lg?.then?.value || "").trim()
      );
      if (anyFormulaEmpty) {
        alert("Logic Calculate membutuhkan formula (mis. Q1 + Q2).");
        return false;
      }
    }

    return true;
  };

  const onAddOrUpdateQuestion = () => {
    if (!validateCurrentQuestion()) return;

    if (editingIndex === null) {
      setSurvey((p) => ({
        ...p,
        questions: [...p.questions, currentQuestion],
      }));
    } else {
      setSurvey((p) => ({
        ...p,
        questions: p.questions.map((q, idx) => (idx === editingIndex ? currentQuestion : q)),
      }));
    }

    // reset editor
    setCurrentQuestion(emptyQuestion());
    setEditingIndex(null);
    setOptionInput("");
    setLogicCondition({ question_key: "", operator: "equals", value: "" });
    setLogicAction({ action: "show", value: "" });
  };

  const onEditQuestion = (idx) => {
    const q = survey.questions[idx];
    setCurrentQuestion({
      client_key: q.client_key || makeQuestionKey(),
      question_text: q.question_text || "",
      type: q.type || "text",
      options: Array.isArray(q.options) ? q.options : [],
      logics: Array.isArray(q.logics) ? q.logics : [],
      calculation: q.calculation || "",
      required: !!q.required,
    });
    setEditingIndex(idx);
    setOptionInput("");
    setLogicCondition({ question_key: "", operator: "equals", value: "" });
    setLogicAction({ action: "show", value: "" });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const removeQuestion = (i) => {
    if (!window.confirm("Hapus pertanyaan ini?")) return;
    const removedKey = survey.questions[i]?.client_key;

    setSurvey((p) => ({
      ...p,
      questions: p.questions
        .filter((_, idx) => idx !== i)
        .map((q) => ({
          ...q,
          logics: (q.logics || []).filter((lg) => {
            if (!removedKey || !lg?.if?.question_key) return true;
            return lg.if.question_key !== removedKey;
          }),
        })),
    }));

    // if removing the one being edited, reset
    if (editingIndex === i) {
      setCurrentQuestion(emptyQuestion());
      setEditingIndex(null);
    }
  };

  const moveQuestion = (from, to) => {
    if (from === to || from < 0 || to < 0 || from >= survey.questions.length || to >= survey.questions.length) {
      return;
    }

    setSurvey((p) => {
      const questions = [...p.questions];
      const [item] = questions.splice(from, 1);
      questions.splice(to, 0, item);
      return { ...p, questions };
    });

    setEditingIndex((idx) => {
      if (idx === null) return idx;
      if (idx === from) return to;
      if (idx === to) return from;
      return idx;
    });
  };

  const handleQuestionDragStart = (idx) => (e) => {
    setDragIndex(idx);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", String(idx));
  };

  const handleQuestionDragOver = (idx) => (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (dragIndex === null || dragIndex === idx) return;
    moveQuestion(dragIndex, idx);
    setDragIndex(idx);
  };

  const handleQuestionDrop = (e) => {
    e.preventDefault();
    setDragIndex(null);
  };

  const handleQuestionDragEnd = () => {
    setDragIndex(null);
  };

  const cancelEdit = () => {
    setCurrentQuestion(emptyQuestion());
    setEditingIndex(null);
    setOptionInput("");
    setLogicCondition({ question_key: "", operator: "equals", value: "" });
    setLogicAction({ action: "show", value: "" });
  };

  // ---------- Save Survey ----------
  const handleSubmit = async () => {
    if (!survey.title.trim() || survey.questions.length === 0) {
      alert("Survey title wajib diisi, dan minimal ada 1 pertanyaan.");
      return;
    }

    const invalidCalculationIndex = survey.questions.findIndex(
      (q) => q.type === "calculation" && !String(q.calculation || "").trim()
    );
    if (invalidCalculationIndex >= 0) {
      alert(`Pertanyaan Kalkulasi Q${invalidCalculationIndex + 1} wajib memiliki formula.`);
      return;
    }

    if (survey.survey_channel === "whatsapp_simple") {
      const badIndex = survey.questions.findIndex((q) => !WHATSAPP_SIMPLE_TYPES.includes(q.type));
      if (badIndex >= 0) {
        alert(`WhatsApp Survey hanya mendukung Text, Number, Select One, dan Select Multiple. Periksa Q${badIndex + 1}.`);
        return;
      }
    }

    const payload = {
      title: survey.title,
      description: survey.description,
      survey_channel: survey.survey_channel || "web",
      questions: survey.questions.map((q, order) => ({
        client_key: q.client_key,
        question_text: q.question_text,
        type: q.type,
        options: q.options,
        logic: refreshLegacyQuestionIds(q.logics, survey.questions), // backend expects 'logic'
        calculation: q.calculation,
        required: q.required,
        order,
      })),
      created_by: user?.id,
      updated_by: user?.id,
    };

    const url = id ? `${API}/surveys/${id}` : `${API}/surveys`;
    const method = id ? "PUT" : "POST";

    try {
      const res = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
          ...authHeaders(user, { Accept: "application/json" }),
        },
        credentials: "include",
        body: JSON.stringify(payload),
      });

      const data = await readJsonSafe(res);
      if (!res.ok || !data?.success) {
        throw new Error(data?.message || `HTTP ${res.status}`);
      }

      alert(id ? "Perubahan tersimpan." : "Survey berhasil dibuat!");
      navigate("/survey-list");
    } catch (e) {
      console.error("Save survey error:", e);
      alert(id ? "Gagal menyimpan perubahan survey." : "Gagal membuat survey.");
    }
  };

  // ---------- UI helpers ----------
  const badgeType = (t) => {
    const hit = QUESTION_TYPES.find((x) => x.value === t);
    return hit?.label || t;
  };

  const renderLogicText = (lg) => {
    const qKey = lg?.if?.question_key;
    const keyIndex = qKey ? survey.questions.findIndex((q) => q.client_key === qKey) : -1;
    const qNo = keyIndex >= 0 ? keyIndex + 1 : lg?.if?.question_id;
    const op = lg?.if?.operator;
    const val = lg?.if?.value;
    const act = lg?.then?.action;
    const formula = lg?.then?.value;

    const opLabel = OPERATORS.find((x) => x.value === op)?.label || op;
    const actLabel = ACTIONS.find((x) => x.value === act)?.label || act;

    return (
      <span>
        <b>If</b> Q{qNo} <b>{opLabel}</b> <b>{String(val)}</b> <b>Then</b>{" "}
        <span className={`sb-pill ${act}`}>
          {actLabel}
        </span>
        {act === "calculate" && formula ? (
          <>
            {" "}
            -&gt; <code>{formula}</code>
          </>
        ) : null}
      </span>
    );
  };

  return (
    <div className="survey-builder">
      <h1>{id ? "Edit Survey" : "Survey Builder"}</h1>

      {loading && <div className="loading">Memuat survey...</div>}

      <div className="survey-form">
        {/* Survey info */}
        <div className="sb-section-head">
          <div>
            <div className="sb-kicker">Survey Info</div>
            <div className="sb-subtitle">
              Buat struktur survey, pertanyaan, dan conditional logic.
            </div>
          </div>
          <div className="sb-counter">
            <div className="sb-counter-num">{survey.questions.length}</div>
            <div className="sb-counter-label">Questions</div>
          </div>
        </div>

        <div className="grid-2">
          <div className="form-group">
            <label>Survey Title</label>
            <input
              type="text"
              value={survey.title}
              onChange={(e) => setSurvey((p) => ({ ...p, title: e.target.value }))}
              placeholder="Contoh: Survey Profil Petani"
            />
            <small>Judul survey yang akan tampil di daftar survey.</small>
          </div>

          <div className="form-group">
            <label>Deskripsi (optional)</label>
            <input
              type="text"
              value={survey.description}
              onChange={(e) => setSurvey((p) => ({ ...p, description: e.target.value }))}
              placeholder="Ringkasan singkat survey"
            />
            <small>Deskripsi singkat untuk membantu admin mengingat isi survey.</small>
          </div>
        </div>

        <div className="form-group">
          <label>Channel Survey</label>
          <div className="sb-channel-grid">
            {SURVEY_CHANNELS.map((channel) => (
              <label
                key={channel.value}
                className={`sb-channel-card ${survey.survey_channel === channel.value ? "active" : ""}`}
              >
                <input
                  type="radio"
                  name="survey_channel"
                  value={channel.value}
                  checked={survey.survey_channel === channel.value}
                  onChange={(e) => {
                    const nextChannel = e.target.value;
                    setSurvey((p) => ({ ...p, survey_channel: nextChannel }));
                    if (
                      nextChannel === "whatsapp_simple" &&
                      !WHATSAPP_SIMPLE_TYPES.includes(currentQuestion.type)
                    ) {
                      setCurrentQuestion((p) => ({
                        ...p,
                        type: "text",
                        options: [],
                        calculation: "",
                      }));
                    }
                  }}
                />
                <span>
                  <strong>{channel.label}</strong>
                  <small>{channel.description}</small>
                </span>
              </label>
            ))}
          </div>
          {hasUnsupportedWhatsAppQuestions ? (
            <small className="sb-warning-text">
              Ada pertanyaan yang belum sesuai untuk WhatsApp Survey. Ubah tipe pertanyaan menjadi Text, Number, Select One, atau Select Multiple sebelum menyimpan.
            </small>
          ) : null}
        </div>

        {/* Add / Edit Question */}
        <h2>{editingIndex === null ? "Add Question" : `Edit Question (Q${editingIndex + 1})`}</h2>

        <div className="question-form">
          <div className="row">
            <div className="form-group" style={{ gridColumn: "1 / -1" }}>
              <label>Question Text</label>
              <input
                type="text"
                value={currentQuestion.question_text}
                onChange={(e) =>
                  setCurrentQuestion((p) => ({ ...p, question_text: e.target.value }))
                }
                placeholder="Contoh: Jenis Kelamin"
              />
            </div>

            <div className="form-group">
              <label>Type</label>
              <select
                value={currentQuestion.type}
                onChange={(e) =>
                  setCurrentQuestion((p) => ({
                    ...p,
                    type: e.target.value,
                    required: e.target.value === "calculation" ? false : p.required,
                    // reset options if not select type
                    options:
                      e.target.value === "select_one" || e.target.value === "select_multiple"
                        ? p.options
                        : [],
                  }))
                }
              >
                {activeQuestionTypes.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>{isCalculationType ? "Status" : "Required"}</label>
              {isCalculationType ? (
                <div className="sb-calculation-status">Dihitung otomatis</div>
              ) : (
                <>
                  <div className="sb-toggle">
                    <input
                      id="sbRequired"
                      type="checkbox"
                      checked={currentQuestion.required}
                      onChange={(e) =>
                        setCurrentQuestion((p) => ({ ...p, required: e.target.checked }))
                      }
                    />
                    <label htmlFor="sbRequired" className="sb-toggle-label">
                      Wajib diisi
                    </label>
                  </div>
                  <small>Jika aktif, user harus mengisi pertanyaan ini.</small>
                </>
              )}
            </div>
          </div>

          {/* Options */}
          {isSelectType && (
            <div className="form-group">
              <label>Options</label>
              <div className="options">
                {currentQuestion.options.map((opt, i) => (
                  <div key={i} className="option">
                    <span>{opt}</span>
                    <button
                      type="button"
                      className="btn-soft"
                      onClick={() => removeOption(i)}
                    >
                      Remove
                    </button>
                  </div>
                ))}

                <div className="add-option">
                  <input
                    type="text"
                    value={optionInput}
                    onChange={(e) => setOptionInput(e.target.value)}
                    placeholder="Tambah pilihan (mis. Pria)"
                  />
                  <button type="button" onClick={addOption}>
                    Add
                  </button>
                </div>
              </div>
              <small>Untuk Select One/Multiple wajib memiliki minimal 1 option.</small>
            </div>
          )}

          {/* Conditional Logic */}
          <div className="form-group">
            <label>Conditional Logic (If -&gt; Then)</label>

            {currentQuestion.logics?.length > 0 && (
              <div className="sb-logic-list">
                {currentQuestion.logics.map((lg, i) => (
                  <div key={i} className="logic-item">
                    {renderLogicText(lg)}
                    <button
                      type="button"
                      className="btn-danger"
                      onClick={() => removeLogic(i)}
                    >
                      Remove
                    </button>
                  </div>
                ))}
              </div>
            )}

            <div className="logic">
              <span>If</span>

              {/* choose question */}
              <select
                value={logicCondition.question_key}
                onChange={(e) =>
                  setLogicCondition((p) => ({ ...p, question_key: e.target.value }))
                }
              >
                <option value="">Pilih pertanyaan sebelumnya...</option>
                {questionChoicesForLogic.map((q) => (
                  <option key={q.value} value={q.value}>
                    {q.label}
                  </option>
                ))}
              </select>

              {/* operator */}
              <select
                value={logicCondition.operator}
                onChange={(e) =>
                  setLogicCondition((p) => ({ ...p, operator: e.target.value }))
                }
              >
                {OPERATORS.map((op) => (
                  <option key={op.value} value={op.value}>
                    {op.label}
                  </option>
                ))}
              </select>

              {/* value */}
              <input
                type="text"
                placeholder="Nilai kondisi (mis. Wanita / 10)"
                value={logicCondition.value}
                onChange={(e) =>
                  setLogicCondition((p) => ({ ...p, value: e.target.value }))
                }
              />

              <span>Then</span>

              {/* action */}
              <select
                value={logicAction.action}
                onChange={(e) =>
                  setLogicAction((p) => ({ ...p, action: e.target.value }))
                }
              >
                {ACTIONS.map((a) => (
                  <option key={a.value} value={a.value}>
                    {a.label}
                  </option>
                ))}
              </select>

              {/* formula (only for calculate) */}
              {logicAction.action === "calculate" ? (
                <input
                  type="text"
                  placeholder="Formula, contoh: Q1 + Q2"
                  value={logicAction.value}
                  onChange={(e) =>
                    setLogicAction((p) => ({ ...p, value: e.target.value }))
                  }
                />
              ) : (
                <div className="sb-empty-slot" />
              )}

              <button type="button" onClick={addLogic}>
                Add Logic
              </button>
            </div>

            <small className="muted">
              Tips formula: gunakan referensi <b>Q1, Q2, ...</b> (berdasarkan urutan pertanyaan).
              <br />
              Contoh: pertanyaan "Nama Perempuan" -&gt; Logic: If Q1 equals <b>Wanita</b> Then <b>Show this question</b>.
              Jika kondisi tidak terpenuhi, pertanyaan otomatis dianggap tidak relevan (hide).
            </small>
          </div>

          {/* Calculation formula; optional on legacy question types. */}
          <div className="form-group">
            <label>{isCalculationType ? "Formula Kalkulasi" : "Calculation Hint (optional)"}</label>
            <input
              type="text"
              value={currentQuestion.calculation}
              onChange={(e) =>
                setCurrentQuestion((p) => ({ ...p, calculation: e.target.value }))
              }
              placeholder={isCalculationType ? "Contoh: Q1 + Q2" : "Mis: Q1 + Q2 (opsional)"}
            />
            <small>
              {isCalculationType ? (
                <>Wajib diisi. Gunakan Q1, Q2, dan seterusnya dengan operator +, -, *, /, atau tanda kurung.</>
              ) : (
                <>Opsional. Jika Anda sudah memakai action <b>Calculate</b> di logic, field ini bisa dikosongkan.</>
              )}
            </small>
          </div>

          {/* Actions */}
          <div className="sb-actions">
            {editingIndex !== null && (
              <button type="button" className="btn-outline" onClick={cancelEdit}>
                Cancel
              </button>
            )}

            <button type="button" onClick={onAddOrUpdateQuestion}>
              {editingIndex === null ? "Add Question" : "Update Question"}
            </button>
          </div>
        </div>

        {/* Questions list */}
        <h2>Questions</h2>
        {survey.questions.length === 0 ? (
          <div className="sb-empty">
            Belum ada pertanyaan. Tambahkan pertanyaan pertama di atas.
          </div>
        ) : (
          <ul className="questions-list">
            {survey.questions.map((q, i) => (
              <li
                key={q.client_key || i}
                className={dragIndex === i ? "is-dragging" : ""}
                onDragOver={handleQuestionDragOver(i)}
                onDrop={handleQuestionDrop}
              >
                <span
                  className="sb-drag-handle"
                  draggable
                  role="button"
                  tabIndex={0}
                  aria-label={`Pindahkan Q${i + 1}`}
                  onDragStart={handleQuestionDragStart(i)}
                  onDragEnd={handleQuestionDragEnd}
                />
                <div>
                  <div className="sb-qhead">
                    <div className="sb-qtitle">
                      <span className="sb-qno">Q{i + 1}</span>
                      <strong>{q.question_text}</strong>
                    </div>
                    <div className="sb-qmeta">
                      <span className="sb-badge">{badgeType(q.type)}</span>
                      {q.required ? <span className="sb-badge req">Required</span> : null}
                    </div>
                  </div>

                  {Array.isArray(q.options) && q.options.length > 0 && (
                    <div className="sb-qsub">
                      Options: <span className="sb-inline">{q.options.join(", ")}</span>
                    </div>
                  )}

                  {Array.isArray(q.logics) && q.logics.length > 0 && (
                    <div className="sb-qsub">
                      Logic:
                      <div className="sb-qlogic">
                        {q.logics.map((lg, j) => (
                          <div key={j} className="sb-qlogic-row">
                            {renderLogicText(lg)}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {q.calculation && (
                    <div className="sb-qsub">
                      {q.type === "calculation" ? "Formula" : "Calculation hint"}: <code>{q.calculation}</code>
                    </div>
                  )}
                </div>

                <div className="sb-item-actions">
                  <button type="button" className="btn-soft" onClick={() => onEditQuestion(i)}>
                    Edit
                  </button>
                  <button type="button" className="btn-danger" onClick={() => removeQuestion(i)}>
                    Remove
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}

        {/* Sticky footer actions */}
        <div className="sb-footer">
          <button type="button" className="btn-outline" onClick={() => navigate("/survey-list")}>
            Back to Survey List
          </button>
          <button type="button" className="submit-btn" onClick={handleSubmit}>
            {id ? "Save Changes" : "Create Survey"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SurveyBuilder;


